import { isProductInStock } from "./productAvailability";

export const PRODUCT_SORT_KEYS = Object.freeze({
  POPULARITY: "popularity",
  RATING: "rating",
  DISCOUNT: "discount",
  PRICE_ASC: "priceAsc",
  PRICE_DESC: "priceDesc",
  NEWEST: "newest",
});

export const DEFAULT_PRODUCT_SORT_KEY = PRODUCT_SORT_KEYS.POPULARITY;

const SORT_KEY_ALIASES = Object.freeze({
  "price-asc": PRODUCT_SORT_KEYS.PRICE_ASC,
  "price-desc": PRODUCT_SORT_KEYS.PRICE_DESC,
  onlyDiscounted: DEFAULT_PRODUCT_SORT_KEY,
  "only-discounted": DEFAULT_PRODUCT_SORT_KEY,
});

const toFiniteNumber = (value) => {
  if (value == null || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const firstPositiveNumber = (values = []) => {
  for (const value of values) {
    const number = toFiniteNumber(value);
    if (number != null && number > 0) return number;
  }
  return null;
};

export const normalizeProductSortKey = (
  value,
  fallback = DEFAULT_PRODUCT_SORT_KEY,
) => {
  const normalized = String(value || "").trim();
  if (!normalized) return fallback;

  const mapped = SORT_KEY_ALIASES[normalized] || normalized;
  const isKnown = Object.values(PRODUCT_SORT_KEYS).includes(mapped);
  return isKnown ? mapped : fallback;
};

export const safeNumber = (value, fallback = 0) => {
  const number = toFiniteNumber(value);
  return number == null ? fallback : number;
};

const resolveRegularPrice = (product) =>
  firstPositiveNumber([
    product?.oldPrice,
    product?.originalPrice,
    product?.compareAtPrice,
    product?.price,
  ]);

const resolveDiscountPrice = (product) =>
  firstPositiveNumber([
    product?.discountPrice,
    product?.currentPrice,
    product?.salePrice,
  ]);

export const getProductRegularPrice = (product) =>
  resolveRegularPrice(product) ?? 0;

export const getProductDiscountPrice = (product) =>
  resolveDiscountPrice(product) ?? 0;

export const getRegularPrice = getProductRegularPrice;
export const getSalePrice = getProductDiscountPrice;

export const getActualProductPrice = (product) => {
  const regularPrice = getProductRegularPrice(product);
  const salePrice = getProductDiscountPrice(product);

  if (
    isProductInStock(product) &&
    regularPrice > 0 &&
    salePrice > 0 &&
    salePrice < regularPrice
  ) {
    return salePrice;
  }

  if (regularPrice > 0) return regularPrice;
  if (salePrice > 0) return salePrice;
  return 0;
};

export const getProductSaving = (product) => {
  const regularPrice = getProductRegularPrice(product);
  const salePrice = getProductDiscountPrice(product);

  if (
    !isProductInStock(product) ||
    regularPrice <= 0 ||
    salePrice <= 0 ||
    salePrice >= regularPrice
  ) {
    return 0;
  }

  return regularPrice - salePrice;
};

export const formatPrice = (value) =>
  new Intl.NumberFormat("uk-UA", {
    maximumFractionDigits: 0,
  }).format(safeNumber(value, 0));

export const getProductEffectivePrice = (product) => {
  const regularPrice = resolveRegularPrice(product);
  const discountPrice = resolveDiscountPrice(product);

  if (regularPrice != null && discountPrice != null && discountPrice < regularPrice) {
    return discountPrice;
  }

  if (regularPrice != null) return regularPrice;
  if (discountPrice != null) return discountPrice;
  return 0;
};

const getComparablePriceForSorting = (product) =>
  getActualProductPrice(product);

export const getProductDiscountPercent = (product) => {
  const regularPrice = resolveRegularPrice(product);
  const discountPrice = resolveDiscountPrice(product);

  if (
    regularPrice == null ||
    discountPrice == null ||
    regularPrice <= 0 ||
    discountPrice <= 0 ||
    discountPrice >= regularPrice
  ) {
    return 0;
  }

  return ((regularPrice - discountPrice) / regularPrice) * 100;
};

export const getDiscountPercent = getProductDiscountPercent;

const calculateRatingFromReviews = (reviews) => {
  if (!Array.isArray(reviews) || reviews.length === 0) return 0;

  const values = reviews
    .map((review) => toFiniteNumber(review?.rating ?? review))
    .filter((rating) => rating != null && rating >= 0);

  if (values.length === 0) return 0;

  const sum = values.reduce((acc, value) => acc + value, 0);
  return sum / values.length;
};

export const getProductRating = (product) => {
  const directRating = [
    product?.averageRating,
    product?.rating,
    product?.reviewStats?.average,
    product?.reviews?.average,
  ]
    .map((value) => toFiniteNumber(value))
    .find((value) => value != null && value >= 0);

  const ratingValue =
    directRating != null
      ? directRating
      : calculateRatingFromReviews(product?.reviews);

  return Math.max(0, ratingValue || 0);
};

export const getProductReviewsCount = (product) => {
  const directCount = [
    product?.reviewsCount,
    product?.reviewCount,
    product?.totalReviews,
    product?.ratingCount,
    product?.reviewStats?.count,
    product?.reviews?.count,
  ]
    .map((value) => toFiniteNumber(value))
    .find((value) => value != null && value >= 0);

  if (directCount != null) {
    return Math.max(0, Math.round(directCount));
  }

  if (Array.isArray(product?.reviews)) {
    return product.reviews.length;
  }

  return 0;
};

export const getProductSalesCount = (product) => {
  const candidates = [
    product?.salesCount,
    product?.soldCount,
    product?.purchasesCount,
    product?.orderCount,
    product?.totalSold,
  ]
    .map((value) => toFiniteNumber(value))
    .filter((value) => value != null && value >= 0);

  if (candidates.length === 0) return 0;
  return Math.max(...candidates);
};

const compareProductsBySortKey = (a, b, sortKey) => {
  const normalizedSortKey = normalizeProductSortKey(sortKey);

  switch (normalizedSortKey) {
    case PRODUCT_SORT_KEYS.RATING: {
      const byRating = getProductRating(b) - getProductRating(a);
      if (byRating !== 0) return byRating;

      const bySales = getProductSalesCount(b) - getProductSalesCount(a);
      if (bySales !== 0) return bySales;

      return 0;
    }
    case PRODUCT_SORT_KEYS.DISCOUNT: {
      const byDiscount =
        getProductDiscountPercent(b) - getProductDiscountPercent(a);
      if (byDiscount !== 0) return byDiscount;

      const bySales = getProductSalesCount(b) - getProductSalesCount(a);
      if (bySales !== 0) return bySales;

      return 0;
    }
    case PRODUCT_SORT_KEYS.PRICE_ASC: {
      const byPrice =
        getComparablePriceForSorting(a) - getComparablePriceForSorting(b);
      if (byPrice !== 0) return byPrice;
      return 0;
    }
    case PRODUCT_SORT_KEYS.PRICE_DESC: {
      const byPrice =
        getComparablePriceForSorting(b) - getComparablePriceForSorting(a);
      if (byPrice !== 0) return byPrice;
      return 0;
    }
    case PRODUCT_SORT_KEYS.NEWEST: {
      const byDate =
        safeNumber(new Date(b?.createdAt).getTime(), 0) -
        safeNumber(new Date(a?.createdAt).getTime(), 0);
      if (byDate !== 0) return byDate;
      return 0;
    }
    case PRODUCT_SORT_KEYS.POPULARITY:
    default: {
      const bySales = getProductSalesCount(b) - getProductSalesCount(a);
      if (bySales !== 0) return bySales;

      const byRating = getProductRating(b) - getProductRating(a);
      if (byRating !== 0) return byRating;

      return 0;
    }
  }
};

export const compareProductsWithStockPriority = (a, b, sortKey) => {
  const leftStockRank = isProductInStock(a) ? 0 : 1;
  const rightStockRank = isProductInStock(b) ? 0 : 1;

  if (leftStockRank !== rightStockRank) {
    return leftStockRank - rightStockRank;
  }

  return compareProductsBySortKey(a, b, sortKey);
};

export const stableSortProducts = (products, comparator) => {
  if (!Array.isArray(products)) return [];

  return products
    .map((product, index) => ({ product, index }))
    .sort((left, right) => {
      const result = comparator(left.product, right.product);
      if (result !== 0) return result;
      return left.index - right.index;
    })
    .map(({ product }) => product);
};
